// Marks <cart-drawer> as .is-updating while any cart request is in flight, so the checkout button shows a
// spinner and can't be used. Counted, because several requests (quantity change + auto gift) can overlap.
if (!window.cartDrawerBusy) {
  let busyCount = 0;
  window.cartDrawerBusy = (delta) => {
    busyCount = Math.max(0, busyCount + delta);
    const drawer = document.querySelector('cart-drawer');
    if (drawer) drawer.classList.toggle('is-updating', busyCount > 0);
  };

  // pointer-events: none stops mouse clicks; this also stops keyboard activation of the button.
  document.addEventListener(
    'click',
    (event) => {
      if (event.target.closest('#CartDrawer-Checkout') && event.target.closest('cart-drawer.is-updating')) {
        event.preventDefault();
      }
    },
    true
  );

  // Quantity changes and removals go through Dawn's CartItems (cart.js).
  if (typeof CartItems !== 'undefined') {
    const { enableLoading, disableLoading } = CartItems.prototype;
    CartItems.prototype.enableLoading = function (line) {
      window.cartDrawerBusy(1);
      return enableLoading.call(this, line);
    };
    CartItems.prototype.disableLoading = function (line) {
      window.cartDrawerBusy(-1);
      return disableLoading.call(this, line);
    };
  }
}

// Rendered by cart-drawer-auto-gift.liquid, one per free gift (data-key). The gifts are $0 products, so:
// - cart qualifies, gift missing  → add it (once; if the shopper removes it, it stays removed)
// - cart qualifies, gift in cart  → remove its paid twin (data-paid-line-key) if that's in the cart too
// - cart qualifies, quantity > 1  → set it back to 1
// - cart stops qualifying         → remove it
// State per gift lives in sessionStorage: 'added' (in the cart) or 'declined' (shopper removed it).
if (!customElements.get('cart-auto-gift')) {
  const pending = {};
  let queue = Promise.resolve();

  const storage = {
    get(key) {
      try {
        return sessionStorage.getItem(`cart-auto-gift:${key}`);
      } catch (e) {
        return null;
      }
    },
    set(key, value) {
      try {
        const name = `cart-auto-gift:${key}`;
        value ? sessionStorage.setItem(name, value) : sessionStorage.removeItem(name);
      } catch (e) {}
    },
  };

  customElements.define(
    'cart-auto-gift',
    class CartAutoGift extends HTMLElement {
      connectedCallback() {
        const key = this.dataset.key;
        if (!key || pending[key]) return;

        const state = storage.get(key);
        const inCart = Boolean(this.dataset.lineKey);

        if (this.dataset.qualifies === 'true') {
          if (inCart) {
            storage.set(key, 'added');
            if (this.dataset.paidLineKey) {
              // The free gift replaces the paid one; quantity is re-checked on the next render.
              this.update(routes.cart_change_url, { id: this.dataset.paidLineKey, quantity: 0 });
            } else if (parseInt(this.dataset.lineQuantity, 10) > 1) {
              this.update(routes.cart_change_url, { id: this.dataset.lineKey, quantity: 1 });
            }
          } else if (state === 'added') {
            storage.set(key, 'declined');
          } else if (state !== 'declined') {
            storage.set(key, 'added');
            this.update(routes.cart_add_url, { items: [{ id: parseInt(this.dataset.variantId, 10), quantity: 1 }] });
          }
        } else {
          if (inCart) this.update(routes.cart_change_url, { id: this.dataset.lineKey, quantity: 0 });
          storage.set(key, null);
        }
      }

      // Runs cart requests one at a time and refreshes the drawer in place, without opening it.
      update(url, body) {
        const key = this.dataset.key;
        const drawer = document.querySelector('cart-drawer');
        const cart = drawer && drawer.getSectionsToRender ? drawer : null;
        pending[key] = true;

        if (cart) {
          body.sections = cart.getSectionsToRender().map((section) => section.id);
          body.sections_url = window.location.pathname;
        }
        const config = fetchConfig('json');
        config.body = JSON.stringify(body);

        window.cartDrawerBusy(1);
        queue = queue
          .then(() => fetch(url, config))
          .then((response) => response.json())
          .then((response) => {
            if (response.status) throw new Error(response.description || response.message);
            pending[key] = false;
            if (!cart || !response.sections) return;
            cart.getSectionsToRender().forEach((section) => {
              const element = section.selector
                ? document.querySelector(section.selector)
                : document.getElementById(section.id);
              const html = response.sections[section.id];
              if (element && html) {
                const content = cart.getSectionInnerHTML(html, section.selector);
                if (window.rushCartSafety) window.rushCartSafety.renderSection(element, content);
                else element.innerHTML = content;
              }
            });
            const overlay = cart.querySelector('#CartDrawer-Overlay');
            if (overlay) overlay.addEventListener('click', cart.close.bind(cart));
          })
          .catch((error) => {
            console.error(error);
            pending[key] = false;
            if (url === routes.cart_add_url) storage.set(key, null);
          })
          .finally(() => window.cartDrawerBusy(-1));
      }
    }
  );
}

// Adds a variant to the cart from inside the cart drawer (empty-state upsell, 2-pack upgrade, add-ons).
// Each [data-cart-add] button carries its own data-variant-id, plus an optional data-gift-variant-id added alongside.
if (!customElements.get('cart-drawer-upsell')) {
  customElements.define(
    'cart-drawer-upsell',
    class CartDrawerUpsell extends HTMLElement {
      connectedCallback() {
        this.cart = document.querySelector('cart-drawer');
        this.querySelectorAll('[data-cart-add]').forEach((button) =>
          button.addEventListener('click', () => this.add(button))
        );
      }

      add(button) {
        if (!this.cart || button.getAttribute('aria-disabled') === 'true') return;
        const message = this.querySelector('.cart-add-error');
        const spinner = button.querySelector('.loading__spinner');

        if (message) message.hidden = true;
        window.cartDrawerBusy(1);
        button.setAttribute('aria-disabled', 'true');
        button.classList.add('loading');
        if (spinner) spinner.classList.remove('hidden');

        const items = [{ id: parseInt(button.dataset.variantId, 10), quantity: 1 }];
        if (button.dataset.giftVariantId) {
          items.push({ id: parseInt(button.dataset.giftVariantId, 10), quantity: 1 });
          // Tells <cart-auto-gift> the gift is already handled, so it doesn't add a second one.
          try {
            sessionStorage.setItem(`cart-auto-gift:${button.dataset.giftKey}`, 'added');
          } catch (e) {}
        }

        const config = fetchConfig('json');
        config.body = JSON.stringify({
          items,
          sections: this.cart.getSectionsToRender().map((section) => section.id),
          sections_url: window.location.pathname,
        });

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) throw new Error(response.description || response.message);
            publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-drawer-upsell', cartData: response });
            // Re-renders #CartDrawer, which replaces this element with the updated cart.
            this.cart.classList.remove('is-empty');
            this.cart.renderContents(response);
          })
          .catch((error) => {
            console.error(error);
            if (message) {
              message.textContent = this.dataset.errorLabel;
              message.hidden = false;
            }
            button.classList.remove('loading');
            button.removeAttribute('aria-disabled');
            if (spinner) spinner.classList.add('hidden');
          })
          .finally(() => window.cartDrawerBusy(-1));
      }
    }
  );
}
